// Browser entry: @rudderstack/analytics-js behind the shared Lissen analytics
// API, for every web surface (link-in-bio, lissen-for-partners, the wer1 claim
// flow, and the mobile app's web build at app.lissen.com).
//
// Lifecycle:
// - `initBrowserAnalytics(config)` is SYNCHRONOUS, idempotent, and never
//   throws. During SSR, or when the write key / data plane URL are absent
//   (previews, local dev), the entry is disabled and every call is a silent
//   no-op.
// - Calls made BEFORE init are buffered (bounded) and replayed inside the
//   init call, with their original `lsn_timestamp` preserved — so route-mount
//   effects can fire events before the root init effect without loss, and no
//   surface needs its own lazy-init workaround.
// - The import is deliberately static and init synchronous, matching the old
//   per-surface modules: once init has run, an event tracked in a click
//   handler reaches the SDK in the same tick, and analytics-js's own
//   persisted queue carries it across a same-tab navigation ("open in app"
//   CTAs navigate away immediately). An async load window here would lose
//   those clicks.

import { RudderAnalytics } from "@rudderstack/analytics-js";
import type {
  ClientAnalyticsConfig,
  IdentifyTraits,
  TrackProperties,
} from "./core/api";
import type { EventName } from "./core/events";
import {
  composeEventProperties,
  getSessionAttribution,
  setSessionAttribution,
} from "./core/context";

export type BrowserAnalyticsConfig = ClientAnalyticsConfig & {
  /** SDK session window; defaults to 30 minutes (all current surfaces). */
  sessionTimeoutMs?: number;
  /**
   * Anonymous id to install BEFORE the SDK loads, so the very first event
   * already carries it (link-in-bio keys its persisted lissen-anonymous-id —
   * the lsn_aid contract with the app — this way). A function form is
   * resolved when init actually loads the SDK.
   */
  initialAnonymousId?: string | (() => string | undefined);
  /**
   * Per-call page-context override, merged over the SDK's auto-captured page
   * context on every track/identify. The wer1 claim flow uses this to redact
   * its URL (the path carries a bearer claim token), and the Live Link to
   * drop a ticket link's token. Undefined leaves the call as it is.
   */
  pageContextOverride?: () => Record<string, unknown> | undefined;
  /** Extra analytics-js load options merged over the defaults. */
  loadOptions?: Record<string, unknown>;
};

const SESSION_TIMEOUT_MS = 1800000; // 30 minutes
const BUFFER_LIMIT = 50;

type BufferedCall =
  | { kind: "track"; name: string; properties: Record<string, unknown> }
  | { kind: "identify"; userId: string; traits?: IdentifyTraits }
  | { kind: "screen"; name: string }
  | { kind: "setAnonymousId"; id: string };

type Status = "idle" | "loaded" | "disabled";

let status: Status = "idle";
let client: RudderAnalytics | null = null;
let config: BrowserAnalyticsConfig | null = null;
let buffer: BufferedCall[] = [];

const reportError = (
  error: unknown,
  context: { operation: string; event?: string },
) => {
  try {
    config?.onError?.(error, context);
  } catch {
    // The error handler itself must never break the page.
  }
};

const callOptions = (): Record<string, unknown> | undefined => {
  const override = config?.pageContextOverride;
  if (!override) return undefined;
  try {
    const page = override();
    return page ? { context: { page } } : undefined;
  } catch {
    return undefined;
  }
};

const push = (call: BufferedCall) => {
  if (buffer.length >= BUFFER_LIMIT) return;
  buffer.push(call);
};

const replay = (calls: BufferedCall[]) => {
  for (const call of calls) {
    switch (call.kind) {
      case "track":
        // Composed at replay (config is only known then); the timestamp
        // captured at call time spreads last, so it survives the stamp.
        deliverTrack(
          call.name,
          config
            ? composeEventProperties({
                surface: config.surface,
                getUserId: config.getUserId,
                properties: call.properties,
              })
            : call.properties,
        );
        break;
      case "identify":
        deliverIdentify(call.userId, call.traits);
        break;
      case "screen":
        deliverScreen(call.name);
        break;
      case "setAnonymousId":
        deliverSetAnonymousId(call.id);
        break;
    }
  }
};

/**
 * Loads the SDK once, synchronously. Safe to call repeatedly and from
 * anywhere (idempotent); subsequent calls are no-ops. On return the entry is
 * either loaded (buffered calls already replayed) or disabled.
 */
export const initBrowserAnalytics = (
  initConfig: BrowserAnalyticsConfig,
): void => {
  if (status !== "idle") return;

  config = initConfig;

  if (
    typeof window === "undefined" ||
    !initConfig.writeKey ||
    !initConfig.dataPlaneUrl
  ) {
    status = "disabled";
    buffer = [];
    return;
  }

  try {
    const instance = new RudderAnalytics();
    const initialAnonymousId =
      typeof initConfig.initialAnonymousId === "function"
        ? initConfig.initialAnonymousId()
        : initConfig.initialAnonymousId;
    if (initialAnonymousId) {
      instance.setAnonymousId(initialAnonymousId);
    }
    instance.load(initConfig.writeKey, initConfig.dataPlaneUrl, {
      sessions: {
        autoTrack: true,
        timeout: initConfig.sessionTimeoutMs ?? SESSION_TIMEOUT_MS,
      },
      ...initConfig.loadOptions,
      // The SDK would otherwise load a polyfill script from a third-party
      // host in browsers missing a feature it checks; the pages' enforced
      // Content-Security-Policy blocks it (it carries no nonce and the host
      // is not allowed), and the SDK then never loads. Third-party scripts
      // stay off payment pages, so the polyfill is never loaded.
      polyfillIfRequired: false,
    });
    client = instance;
    status = "loaded";
    const pending = buffer;
    buffer = [];
    replay(pending);
  } catch (error) {
    // SDK construction/load must never break the page. Return to idle so a
    // later init call can retry.
    client = null;
    status = "idle";
    reportError(error, { operation: "init" });
  }
};

// The SDK's track/identify/page overloads are ambiguous for the
// (name, properties, options) shapes we use — same cast every surface wrapper
// made before unification.
type SdkCall = (
  ...args: (string | Record<string, unknown> | undefined)[]
) => void;

const deliverTrack = (name: string, properties: Record<string, unknown>) => {
  try {
    (client?.track as SdkCall | undefined)?.(name, properties, callOptions());
  } catch (error) {
    reportError(error, { operation: "track", event: name });
  }
};

const deliverIdentify = (userId: string, traits?: IdentifyTraits) => {
  try {
    // identify has no event `properties`; the surface stamp rides as a trait
    // (lands in context.traits, which the ingest reads) so identify rows are
    // still attributable.
    (client?.identify as SdkCall | undefined)?.(
      userId,
      { ...traits, lissen_client: config?.surface },
      callOptions(),
    );
  } catch (error) {
    reportError(error, { operation: "identify" });
  }
};

const deliverScreen = (name: string) => {
  try {
    (client?.page as SdkCall | undefined)?.(
      name,
      { name, lissen_client: config?.surface },
      callOptions(),
    );
  } catch (error) {
    reportError(error, { operation: "screen" });
  }
};

const deliverSetAnonymousId = (id: string) => {
  try {
    client?.setAnonymousId(id);
  } catch (error) {
    reportError(error, { operation: "setAnonymousId" });
  }
};

/** Catalog-typed; a wrong name or payload is a build error. */
export const track = <E extends EventName>(
  name: E,
  properties: TrackProperties<E>,
): void => {
  if (status === "disabled") return;
  if (status !== "loaded" || !config) {
    // Stamp the timestamp now so the buffered event reflects when it actually
    // happened; composition at replay spreads it last, so it wins the stamp.
    push({
      kind: "track",
      name,
      properties: {
        lsn_timestamp: new Date().toISOString(),
        ...(properties as Record<string, unknown>),
      },
    });
    return;
  }
  deliverTrack(
    name,
    composeEventProperties({
      surface: config.surface,
      getUserId: config.getUserId,
      properties: properties as Record<string, unknown>,
    }),
  );
};

export const identify = (userId: string, traits?: IdentifyTraits): void => {
  if (status === "disabled") return;
  if (status !== "loaded") {
    push({ kind: "identify", userId, traits });
    return;
  }
  deliverIdentify(userId, traits);
};

/** Records a page view (the SDK `page` call; named for cross-surface parity). */
export const screen = (name: string): void => {
  if (status === "disabled") return;
  if (status !== "loaded") {
    push({ kind: "screen", name });
    return;
  }
  deliverScreen(name);
};

export const getAnonymousId = (): string | null => {
  if (status !== "loaded" || !client) return null;
  try {
    return client.getAnonymousId() ?? null;
  } catch {
    return null;
  }
};

export const setAnonymousId = (id: string): void => {
  if (status === "disabled" || !id) return;
  if (status !== "loaded") {
    push({ kind: "setAnonymousId", id });
    return;
  }
  deliverSetAnonymousId(id);
};

/** The SDK-persisted identified user id (set by identify, cleared by reset). */
export const getIdentifiedUserId = (): string | null => {
  if (status !== "loaded" || !client) return null;
  try {
    return client.getUserId() ?? null;
  } catch {
    return null;
  }
};

/**
 * Clears the authenticated identity and starts a new journey.
 *
 * With `nextAnonymousId`, also rotates the anonymous id (the SDK clears its
 * own, then the caller-supplied id is installed — surfaces that persist their
 * anonymous id outside the SDK, like link-in-bio, must rotate that copy first
 * and pass the fresh id here, otherwise the old id would repopulate on
 * reload). Without it, the anonymous id is kept (mobile-web logout semantics).
 */
export const resetIdentity = (options?: { nextAnonymousId?: string }): void => {
  // Deliberately not buffered: replaying a logout after a later init could
  // clear an identity that was never loaded. No-op unless the SDK is live.
  if (status !== "loaded" || !client) return;
  const next = options?.nextAnonymousId;
  try {
    if (next) {
      client.reset({ entries: { anonymousId: true } });
    } else {
      client.reset();
    }
  } catch (error) {
    reportError(error, { operation: "resetIdentity" });
  }
  if (next) {
    deliverSetAnonymousId(next);
  }
};

/**
 * Rotates identity only when an SDK user is actually set: a stale
 * authenticated session is split, while a never-authenticated guest keeps
 * their existing anonymous journey. Waits for SDK readiness (the persisted
 * user id is only readable then).
 */
export const resetIdentityIfAuthenticated = (options?: {
  nextAnonymousId?: () => string;
}): void => {
  if (status !== "loaded" || !client) return;
  const instance = client;
  try {
    instance.ready(() => {
      try {
        if (instance.getUserId()) {
          resetIdentity({ nextAnonymousId: options?.nextAnonymousId?.() });
        }
      } catch (error) {
        reportError(error, { operation: "resetIdentityIfAuthenticated" });
      }
    });
  } catch (error) {
    reportError(error, { operation: "resetIdentityIfAuthenticated" });
  }
};

export { getSessionAttribution, setSessionAttribution };
export type { SessionAttribution } from "./core/context";
export type { EventName, Events, LissenClient } from "./core/events";
export type { TrackProperties } from "./core/api";

/** Test-only: returns the entry to its pristine pre-init state. */
export const resetBrowserAnalyticsForTests = (): void => {
  status = "idle";
  client = null;
  config = null;
  buffer = [];
};
